/**
 * Carga perezosa del bundle del builder. El SDK de producción (`veo.js`) es
 * lo único que el cliente integra; cuando el dashboard abre la app con
 * `?veoBuilder=<token>`, este loader inyecta `veo-builder.js` (que se
 * auto-conecta). Así el modo no-code "simplemente funciona" sin que el
 * cliente tenga que instalar nada extra.
 *
 * No importa nada del plugin builder para no arrastrar su código al bundle
 * de producción (ver budget en CLAUDE.md). El nombre del query param debe
 * coincidir con `BUILDER_TOKEN_PARAM` de `plugins/builder/bridge-protocol`.
 */
import { hasDocument, hasWindow } from '../utils/safe-env';
import { resolveBuilderToken } from './builder-token';

/**
 * `true` cuando la app corre en modo builder (URL con `?veoBuilder=<token>`, o
 * el token persistido en sessionStorage tras un redirect de auth). En ese modo
 * el editor pinta las guías vía PREVIEW; el runtime NO debe resolver/renderizar
 * las guías `live` o se duplicarían (la live del runtime + la preview del editor).
 */
export { isBuilderMode } from './builder-token';

export function maybeLoadBuilder(opts: {
  builderUrl?: string;
  debug?: boolean;
  apiKey?: string;
  apiUrl?: string;
  /**
   * Fallback para integraciones por bundler (sin `<script>` de dónde derivar la
   * URL): carga el builder con un `import()` dinámico. Lo pasa SOLO el entry
   * ESM/CJS (`index.ts`), así el bundler lo code-splitea (cero costo en prod, se
   * descarga solo en modo builder). El IIFE NO lo pasa (usa el `<script>`).
   */
  loadBuilder?: () => Promise<unknown>;
}): void {
  if (!hasWindow() || !hasDocument()) return;

  // Lee el token de la URL o de sessionStorage (sobrevive al redirect que tira
  // el query string) y persiste el de la URL apenas lo ve. Sin token → no-op.
  if (resolveBuilderToken() === null) return;

  // Dejamos apiKey/apiUrl en un global para que el modo builder VERIFIQUE el
  // token contra el backend (`/v1/builder/verify`) antes de activarse. Solo se
  // setea cuando el builder se carga desde `init()` (integración real); en
  // demos que cargan veo-builder.js a mano no existe → verificación best-effort.
  if (opts.apiKey && opts.apiUrl) {
    (window as unknown as { __veoBuilder?: unknown }).__veoBuilder = {
      apiKey: opts.apiKey,
      apiUrl: opts.apiUrl,
    };
  }

  // Ya cargado (el IIFE expone window.veoBuilder).
  if ((window as unknown as { veoBuilder?: unknown }).veoBuilder) return;

  const url = opts.builderUrl ?? deriveBuilderUrl();
  if (url) {
    if (document.querySelector(`script[src="${url}"]`)) return;
    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    if (opts.debug) {
      script.addEventListener('error', () => console.error('[veo] no pude cargar', url));
    }
    document.head.appendChild(script);
    return;
  }

  // Integración por bundler: no hay `<script>` que derivar. Cargamos el builder
  // con el `import()` dinámico que pasó el entry ESM/CJS. El consumidor NO tiene
  // que copiar `veo-builder.js` ni setear `builderUrl`: "simplemente funciona".
  if (opts.loadBuilder) {
    void opts.loadBuilder().catch((err) => {
      if (opts.debug) console.error('[veo] no pude cargar el builder (import dinámico)', err);
    });
    return;
  }

  if (opts.debug) {
    console.warn('[veo] builder mode pedido pero no pude resolver la URL de veo-builder.js');
  }
}

/** Deriva `veo-builder.js` del `<script>` que cargó `veo.js`. */
function deriveBuilderUrl(): string | null {
  const scripts = document.querySelectorAll<HTMLScriptElement>('script[src]');
  for (const script of Array.from(scripts)) {
    if (/\bveo(\.min)?\.js(\?.*)?$/.test(script.src)) {
      return script.src.replace(/\bveo(\.min)?\.js/, 'veo-builder$1.js');
    }
  }
  return null;
}
